// pattern: Imperative Shell

import { html } from 'htm/preact'
import { type FunctionComponent, type VNode } from 'preact'
import { useCallback, useRef, useState } from 'preact/hooks'
import { CheckBox } from '@substrate-system/check-box'
import { ToolTip } from '@substrate-system/tool-tip'
import { RadioInput } from '@substrate-system/radio-input'
import { DetailsSummary } from '@substrate-system/details-summary'
import { State, type SchemaTarget } from '../state.js'
import { IconCheck } from '../components/icon-check.js'
import { IconPlus } from '../components/icon-plus.js'
import { IconX } from '../components/icon-x.js'
import { IconTrash } from '../components/icon-trash.js'
import {
    SUPPORTED_METHODS,
    listRouteEntries,
    parsePathParameters,
    isRouteDraftComplete,
    routeDraftHasPendingChanges,
    parseRequestBody,
    buildResponses,
    summarizeResponseStatuses,
    isJsonMediaType,
    isXmlMediaType,
    inferSchemaFromExample,
    mediaTypeKind,
    validateSchemaModel,
    emptySchemaNode,
    CURATED_STATUS_CODES,
    CURATED_MEDIA_TYPES,
    REQUEST_BODY_MEDIA_TYPES,
    type HttpMethod,
    type MethodDraft,
    type ParameterEntry,
    type ResponseEntry,
    type SchemaPath,
    type SchemaNode,
    type SchemaNodeType,
    type StringFormat,
    type PropertyEntry,
} from '../lib/openapi-document.js'
import { highlightJson } from '../lib/prism.js'
import { Button } from '../components/button.js'
import {
    MethodSectionTransition
} from '../components/method-section-transition.js'
import './home.css'

export const HomeRoute:FunctionComponent<{
    state:ReturnType<typeof State>
}> = function HomeRoute ({ state }) {
    const onSaveRoute = useCallback((ev:Event) => {
        ev.preventDefault()
        State.SubmitRoute(state)
        // On success the draft and selection are left in place (the form does
        // NOT collapse), so the page never shrinks and the viewport never
        // moves. Return focus to the path input so keyboard users land on a
        // known, stable control (WCAG 2.4.3); { preventScroll: true } keeps
        // the viewport exactly where the user was working rather than
        // scrolling the input into view.
        if (!state.routeDraft.value.error) {
            focusAfterRender(
                () => document.getElementById('route-path'),
                { preventScroll: true }
            )
        }
    }, [])

    const onPathInput = useCallback((ev:Event) => {
        const target = ev.currentTarget as HTMLInputElement
        State.UpdatePath(state, target.value)
    }, [])

    const onSelectRoute = useCallback((path:string) => {
        State.SelectRoute(state, path)
    }, [])

    // Start a brand-new route: discard selection + unsaved draft edits (no
    // confirmation) and move focus to the path input so typing can begin.
    const onStartNewRoute = useCallback((ev:Event) => {
        ev.preventDefault()
        State.StartNewRoute(state)
        focusAfterRender(() =>
            document.getElementById('route-path')
        )
    }, [])

    // Open the inline removal confirmation, then focus its "remove" button
    // (the row is matched by its stable data-path, not its shifting index).
    const onBeginRemoveRoute = useCallback((path:string) => {
        State.BeginRemoveRoute(state, path)
        focusAfterRender(() => {
            const confirms = Array.from(
                document.querySelectorAll<HTMLElement>(
                    '.route-remove-confirm'
                )
            )
            const match = confirms.find(c => c.dataset.path === path)
            return match?.querySelector<HTMLElement>(
                '.route-remove-confirm-yes'
            )
        })
    }, [])

    // Dismiss the inline confirmation, then return focus to that row's
    // now-restored remove "x".
    const onCancelRemoveRoute = useCallback((path:string) => {
        State.CancelRemoveRoute(state, path)
        focusAfterRender(() => {
            const removes = Array.from(
                document.querySelectorAll<HTMLElement>(
                    '.route-list .route-remove'
                )
            )
            return removes.find(b => b.dataset.path === path)
        })
    }, [])

    // Confirm removal, then move focus to the remove "x" of the entry now
    // occupying the deleted row's position (the next remaining entry), else
    // the previous entry's, else the route-path input when the list is empty.
    const onConfirmRemoveRoute = useCallback((
        path:string,
        entryIndex:number
    ) => {
        State.ConfirmRemoveRoute(state, path)
        focusAfterRender(() => {
            const buttons = Array.from(
                document.querySelectorAll<HTMLElement>(
                    '.route-list .route-remove'
                )
            )
            if (buttons.length === 0) {
                return document.getElementById('route-path')
            }
            return buttons[Math.min(entryIndex, buttons.length - 1)]
        })
    }, [])

    const onCopy = useCallback(async (ev:MouseEvent) => {
        ev.preventDefault()
        await State.CopyDocument(state)
    }, [])

    const onTitleInput = useCallback((ev:Event) => {
        const target = ev.currentTarget as HTMLInputElement
        State.UpdateTitle(state, target.value)
    }, [])

    const entries = listRouteEntries(state.document.value)
    const selectedPath = state.selectedRoute.value?.originalPath
    const pendingRemovals = state.pendingRemovals.value
    const routeDraft = state.routeDraft.value
    const copyVersion = state.copyVersion.value
    const documentText = state.documentText.value
    const apiTitle = state.document.value.info.title

    const selectedMethods = routeDraft.selectedMethods
    const pathParameters = routeDraft.pathParameters

    // Disclosure remount key. Keyed on the edit-session token (NOT the selected
    // path) so saving a new route -- which re-points selectedRoute and would
    // otherwise change this key -- does not remount/collapse the form and
    // scroll the viewport. The token only advances when a genuinely new editing
    // context begins (load/new/remove), so disclosures still reset then.
    const routeContext = String(state.routeEditSession.value)

    // Previous-render snapshot, used to detect which methods the user just
    // toggled. Render-time ref diff (same pattern as useInitialOpen) keeps the
    // trigger logic in the view and state.ts pure.
    const prevRender = useRef<{
        routeContext:string;
        selectedMethods:string[];
        operations:typeof routeDraft.operations;
    } | null>(null)

    // Methods whose section is collapsing out. Held in a ref (mutated during
    // render) so a just-left method can join the rendered list in the SAME
    // render its live node leaves — no one-frame gap (design risk #2). The
    // tick state lets onExited force a re-render to prune a finished snapshot.
    const exitingRef = useRef<Map<string, MethodDraft>>(new Map())
    const [, forcePrune] = useState(0)

    const prev = prevRender.current
    const sameContext = prev !== null && prev.routeContext === routeContext
    const exiting = exitingRef.current

    let entering:Set<string>
    if (sameContext) {
        entering = new Set<string>(
            selectedMethods.filter(m => !prev!.selectedMethods.includes(m))
        )
        // Re-check mid-exit: a re-selected method cancels its collapse.
        for (const m of selectedMethods) {
            if (exiting.has(m)) exiting.delete(m)
        }
        // Leaving = methods in the previous render but not now. Snapshot each
        // from the PREVIOUS render's operations (ToggleMethod already deleted
        // it from live state).
        for (const m of prev!.selectedMethods) {
            if (!selectedMethods.includes(m)) {
                const snap = prev!.operations[m] ?? exiting.get(m)
                if (snap) exiting.set(m, snap)
            }
        }
    } else {
        // Route load / switch / reset: render everything instantly and drop
        // any in-flight snapshots from the previous context.
        entering = new Set<string>()
        exiting.clear()
    }

    // Record THIS render for the next diff.
    prevRender.current = {
        routeContext,
        selectedMethods,
        operations: routeDraft.operations,
    }

    // Methods to render: live methods plus any still-collapsing snapshots,
    // in canonical order so a leaving section keeps its slot.
    const exitingOnly = [...exiting.keys()].filter(
        m => !selectedMethods.includes(m)
    )
    const renderMethods = canonicalMethodOrder(
        [...selectedMethods, ...exitingOnly]
    )

    const initialHttpMethodsOpen = useInitialOpen(
        `http-methods-${routeContext}`, selectedMethods.length === 0
    )

    const bodyCapableMethods = ['post', 'put', 'patch', 'delete']

    const anyRequestBodyInvalid = selectedMethods.some(method => {
        const op = routeDraft.operations[method]
        if (!op || !bodyCapableMethods.includes(method)) return false
        const kind = mediaTypeKind(
            op.requestBodyMediaType || 'application/json'
        )
        if (kind === 'object') {
            return !!op.requestBodySchemaModel &&
                validateSchemaModel(op.requestBodySchemaModel) !== null
        }
        if (kind === 'json') {
            const schemaBad = op.requestBodySchema.trim() !== '' &&
                !parseRequestBody(op.requestBodySchema).ok
            const exampleBad = op.requestBodyExample.trim() !== '' &&
                !parseRequestBody(op.requestBodyExample).ok
            return schemaBad || exampleBad
        }
        return false
    })

    const anyResponsesInvalid = selectedMethods.some(method => {
        const op = routeDraft.operations[method]
        if (!op) return false
        if (!buildResponses(op.responses).ok) return true
        const statuses = op.responses
            .map(r => r.status.trim())
            .filter(s => s !== '')
        if (statuses.some((s, idx) => statuses.indexOf(s) !== idx)) {
            return true
        }
        return op.responses.some(resp =>
            mediaTypeKind(resp.mediaType || 'application/json') ===
                'object' &&
            !!resp.schemaModel &&
            validateSchemaModel(resp.schemaModel) !== null
        )
    })

    // The Save button is dead unless the draft both differs from the saved
    // route (or, for a new route, from an empty draft) AND is valid/complete.
    // Without the pending-changes gate the button would be enabled with nothing
    // to save -- e.g. immediately after a save, or right after loading a route.
    const hasPendingChanges = routeDraftHasPendingChanges(
        state.document.value,
        selectedPath ?? null,
        routeDraft
    )

    const submitDisabled = !hasPendingChanges ||
        !isRouteDraftComplete(routeDraft) ||
        anyRequestBodyInvalid ||
        anyResponsesInvalid

    const pathTokenMap = new Map(
        parsePathParameters(routeDraft.pathInput)
            .map(t => [t.name, t.optional])
    )

    return html`<section class="route home">
        <div class="home-grid">
            <section class="route-list-panel route-sidebar">
                <div class="route-list-header">
                    <h2>Routes</h2>
                    <${ToolTip.TAG}
                        delay=${1000}
                        content="Add a new route"
                    >
                        <button type="button"
                            class="btn icon square route-new plus"
                            aria-label="Add a new route"
                            onClick=${onStartNewRoute}
                        ><${IconPlus} /></button>
                    <//>
                </div>
                <ul class="route-list">
                    ${entries.length === 0 ?
                        html`<li class="route-list-empty">
                            No routes added yet.
                        </li>` :
                        entries.map((entry, entryIndex) => {
                            const methods = SUPPORTED_METHODS.filter(
                                m => entry.operations[m as HttpMethod]
                            )
                            const isSelected = selectedPath === entry.path
                            const isPending = pendingRemovals.includes(
                                entry.path
                            )
                            return html`<li class="route-entry">
                                <div class="route-entry-header${
                                    isSelected ? ' selected' : ''
                                }">
                                    <button
                                        type="button"
                                        class="route-select"
                                        aria-pressed=${
                                            isSelected ? 'true' : 'false'
                                        }
                                        onClick=${() =>
                                            onSelectRoute(entry.path)}
                                    >
                                        <h3>${entry.path}</h3>
                                    </button>
                                    ${isPending ?
                                        html`<div
                                            class="route-remove-confirm"
                                            data-path=${entry.path}
                                        >remove this route?<button
                                            type="button"
                                            class="btn danger route-remove-confirm-yes"
                                            aria-label=${
                                                'Remove this route, ' +
                                                entry.path
                                            }
                                            onClick=${() =>
                                                onConfirmRemoveRoute(
                                                    entry.path, entryIndex
                                                )}
                                        >remove</button>
                                        <${ToolTip.TAG}
                                            content="Cancel removal"
                                            distance="4px"
                                            delay="500"
                                        >
                                            <button
                                                type="button"
                                                class="btn icon route-remove-confirm-no icon-x"
                                                aria-label=${
                                                    'Cancel removing route ' +
                                                    entry.path
                                                }
                                                onClick=${() =>
                                                    onCancelRemoveRoute(
                                                        entry.path
                                                    )}
                                            >
                                                <${IconX} />
                                                <span class="visually-hidden">
                                                    cancel removal
                                                </span>
                                            </button>
                                        <//>
                                        </div>` :
                                        html`<button
                                            type="button"
                                            class="btn icon route-remove icon-x"
                                            data-path=${entry.path}
                                            aria-label=${
                                                'Remove route ' + entry.path
                                            }
                                            onClick=${() =>
                                                onBeginRemoveRoute(entry.path)}
                                        ><${IconX} /></button>`}
                                </div>
                                <ul class="route-methods-list">
                                    ${methods.map(method =>
                                        html`<li>
                                            <span class="method-badge
                                                method-${method}">
                                                ${method.toUpperCase()}
                                            </span>
                                            <span>${
                                                entry.operations[
                                                    method as HttpMethod
                                                ]?.summary
                                            }</span>
                                        </li>`
                                    )}
                                </ul>
                            </li>`
                        })}
                </ul>
            </section>

            <section class="authoring-panel">
                <h2>Route Editor</h2>

                <form class="route-form" onSubmit=${onSaveRoute}>
                    <div class="field">
                        <label for="route-path">Route path</label>
                        <input
                            id="route-path"
                            class="input"
                            type="text"
                            name="route-path"
                            value=${routeDraft.pathInput}
                            onInput=${onPathInput}
                            placeholder="/pets"
                            aria-invalid=${routeDraft.error ? 'true' : 'false'}
                            aria-describedby=${routeDraft.error ?
                                'route-path-error' :
                                undefined
                            }
                        />
                    </div>

                    ${pathParameters.length > 0 ?
                        pathParametersSection(
                            state,
                            pathParameters,
                            pathTokenMap,
                            false,
                            `path-params-${routeContext}`
                        ) :
                        null}

                    ${disclosureSection({
                        title: 'HTTP methods',
                        open: initialHttpMethodsOpen,
                        sectionKey: `http-methods-${routeContext}`,
                        contentClass: 'method-group',
                        children: html`
                            <div class="method-options">
                                ${SUPPORTED_METHODS.map(method => {
                                    const id = `method-${method}`
                                    return html`<${CheckBox.TAG}
                                            id=${id}
                                            type="checkbox"
                                            checked=${routeDraft
                                                .selectedMethods
                                                .includes(method)
                                            }
                                            onChange=${() =>
                                                State.ToggleMethod(
                                                    state, method
                                                )}
                                        >
                                            <span
                                            >${method.toUpperCase()}</span>
                                        <//>
                                    `
                                })}
                            </div>
                        `
                    })}

                    ${renderMethods.map(method => {
                        const isExiting = exiting.has(method) &&
                            !selectedMethods.includes(method)
                        if (isExiting) {
                            const snap = exiting.get(method)
                            return html`
                                <${MethodSectionTransition}
                                    key=${`${method}-exit`}
                                    method=${method}
                                    enter=${false}
                                    exit=${true}
                                    onExited=${() => {
                                        exitingRef.current.delete(method)
                                        forcePrune(n => n + 1)
                                    }}
                                >${methodSection(
                                    state, method, routeContext, snap
                                )}<//>
                            `
                        }
                        return html`
                            <${MethodSectionTransition}
                                key=${method}
                                method=${method}
                                enter=${entering.has(method)}
                                exit=${false}
                                onExited=${() => {}}
                            >${methodSection(
                                state, method, routeContext
                            )}<//>
                        `
                    })}


                    ${routeDraft.error ?
                        html`<p
                            id="route-path-error"
                            class="message error"
                            role="alert"
                        >${routeDraft.error}</p>` :
                        null}

                    <div class="controls">
                        <button
                            class="btn"
                            type="submit"
                            disabled=${submitDisabled}
                        >Save Route</button>
                    </div>

                    <p class="message status" role="status">
                        ${routeDraft.copiedMessage ||
                            routeDraft.successMessage}
                    </p>
                </form>
            </section>
        </div>

        <section class="document-output">
            <div class="field api-title-field">
                <label for="api-title">API title</label>
                <input id="api-title"
                    type="text"
                    name="api-title"
                    value=${apiTitle}
                    onInput=${onTitleInput}
                />
            </div>

            <div class="output-header">
                <h2 id="oapi-doc">OpenAPI Document</h2>
                <div class="copy-controls">
                    ${copyVersion > 0 ?
                        html`<span
                            key=${copyVersion}
                            class="copy-check"
                            aria-hidden="true"
                        >
                            <${IconCheck} />
                        </span>` :
                        null}
                    <button
                        class="btn"
                        onClick=${onCopy}
                    >Copy JSON</button>
                    <span
                        class="visually-hidden"
                        aria-live="polite"
                    >
                        ${copyVersion > 0 ?
                            'Copied JSON to the clipboard.' :
                            ''}
                    </span>
                </div>
            </div>

            <label class="copy-output" for="document-json">
                Copy-ready JSON
            </label>
            <textarea id="document-json" readOnly value=${documentText}></textarea>
        </section>
    </section>`
}

// Canonical (SUPPORTED_METHODS) order for a set of method names. The view
// needs its own copy because state.ts's inCanonicalOrder is not exported and
// state.ts must stay untouched. Used in Phase 3; kept here for next phase's
// merged live+exiting list.
function canonicalMethodOrder (methods:string[]):string[] {
    return SUPPORTED_METHODS.filter(m => methods.includes(m))
}

function methodScope (method:string):HTMLElement | null {
    // :not([data-exiting]) so focus queries never resolve into an inert,
    // collapsing snapshot during a re-check race.
    return document.querySelector<HTMLElement>(
        `.method-section[data-method="${method}"]:not([data-exiting])`
    )
}

function firstControlOfLastRow (
    method:string,
    rowSelector:string
):HTMLElement | null | undefined {
    const scope = methodScope(method)
    if (!scope) return null
    const rows = scope.querySelectorAll<HTMLElement>(rowSelector)
    const last = rows[rows.length - 1]
    return last?.querySelector<HTMLElement>('input, select, textarea')
}

function pathParametersSection (
    state:ReturnType<typeof State>,
    pathParameters:ParameterEntry[],
    pathTokenMap:Map<string, boolean>,
    open:boolean,
    sectionKey:string
) {
    return disclosureSection({
        title: 'Path parameters',
        open,
        sectionKey,
        contentClass: 'parameters-section path-parameters-section',
        children: html`${pathParameters.map((param, i) => {
            const isOptional = pathTokenMap.get(param.name) ?? false
            const onType = (ev:Event) =>
                State.UpdatePathParameterField(
                    state, i, 'type',
                    (ev.currentTarget as HTMLSelectElement).value
                )
            const onDesc = (ev:Event) =>
                State.UpdatePathParameterField(
                    state, i, 'description',
                    (ev.currentTarget as HTMLInputElement).value
                )
            const typeId = `path-param-${i}-type`
            const typeLabel =
                `Type, path parameter ${param.name}`
            const descId = `path-param-${i}-description`
            const descLabel =
                `Description, path parameter ${param.name}`
            return html`<div class="parameter-row path-param">
                <div class="param-name-cell">
                    <span class="field-label">Name</span>
                    <div class="param-name-area">
                        <span class="param-name"
                        >${param.name}</span>
                        ${isOptional ?
                            html`<span
                                class="param-optional-tag"
                            >optional</span>` :
                            null}
                    </div>
                </div>
                <div class="param-field">
                    <label
                        class="field-label"
                        for=${typeId}
                    >Type</label>
                    <select
                        id=${typeId}
                        name=${typeId}
                        aria-label=${typeLabel}
                        value=${param.type}
                        onChange=${onType}
                    >
                        <option value="string">
                            string
                        </option>
                        <option value="number">
                            number
                        </option>
                        <option value="integer">
                            integer
                        </option>
                        <option value="boolean">
                            boolean
                        </option>
                    </select>
                </div>
                <div class="param-field">
                    <label
                        class="field-label"
                        for=${descId}
                    >Description</label>
                    <input
                        type="text"
                        id=${descId}
                        name=${descId}
                        aria-label=${descLabel}
                        value=${param.description}
                        placeholder="description"
                        onInput=${onDesc}
                    />
                </div>
            </div>`
        })}`,
    })
}

function methodSection (
    state:ReturnType<typeof State>,
    method:string,
    routeContext:string,
    opOverride?:MethodDraft
) {
    const M = method.toUpperCase()
    const op = opOverride ?? state.routeDraft.value.operations[method]
    if (!op) return null
    const isBodyCapable =
        ['post', 'put', 'patch', 'delete'].includes(method)

    // ----- per-method handlers (plain closures over `method`) -----
    const onSummaryInput = (ev:Event) => {
        const t = ev.currentTarget as HTMLInputElement
        State.UpdateMethodSummary(state, method, t.value)
    }
    const onAddQueryParameter = () => {
        State.AddQueryParameter(state, method)
        focusAfterRender(() => firstControlOfLastRow(
            method, '.parameter-row.query-param'
        ))
    }
    const onAddResponse = () => {
        State.AddResponse(state, method)
        focusAfterRender(() => firstControlOfLastRow(
            method, '.response-item'
        ))
    }
    const onRequestSchemaInput = (ev:Event) => {
        const t = ev.currentTarget as HTMLTextAreaElement
        State.UpdateRequestBodyField(state, method, 'schema', t.value)
    }
    const onRequestExampleInput = (ev:Event) => {
        const t = ev.currentTarget as HTMLTextAreaElement
        State.UpdateRequestBodyField(state, method, 'example', t.value)
    }

    // ----- per-method submit-gating bits for INLINE errors -----
    const requestBodyMediaType =
        op.requestBodyMediaType || 'application/json'
    const requestBodyIsJson = isJsonMediaType(requestBodyMediaType)
    const requestSchemaParse = parseRequestBody(op.requestBodySchema)
    const requestExampleParse = parseRequestBody(op.requestBodyExample)
    const requestSchemaInvalid = isBodyCapable &&
        op.requestBodySchema.trim() !== '' && !requestSchemaParse.ok
    const requestExampleInvalid = isBodyCapable && requestBodyIsJson &&
        op.requestBodyExample.trim() !== '' && !requestExampleParse.ok
    const requestBodyKind = mediaTypeKind(requestBodyMediaType)
    const requestBodyModelError =
        isBodyCapable && requestBodyKind === 'object' &&
        op.requestBodySchemaModel ?
            validateSchemaModel(op.requestBodySchemaModel) :
            null

    const responsesBuild = buildResponses(op.responses)
    const responseStatuses = op.responses
        .map(r => r.status.trim()).filter(s => s !== '')
    const duplicateStatus = responseStatuses.find(
        (s, idx) => responseStatuses.indexOf(s) !== idx
    )
    const responseModelError = op.responses
        .map(resp =>
            mediaTypeKind(resp.mediaType || 'application/json') ===
                'object' && resp.schemaModel ?
                validateSchemaModel(resp.schemaModel) :
                null)
        .find(e => e) ?? null
    const responsesError = !responsesBuild.ok ?
        responsesBuild.error :
        (duplicateStatus ?
            `Duplicate response status code ${duplicateStatus}.` :
            responseModelError)
    const responsesInvalid = responsesError !== null
    const responsesSummary = summarizeResponseStatuses(op.responses)

    return disclosureSection({
        title: M,
        open: false,
        sectionKey: `method-${routeContext}-${method}`,
        contentClass: 'method-section-content',
        children: html`
                <div class="field">
                    <label class="field-label"
                        for=${`${method}-summary`}
                    >Summary</label>
                    <input
                        id=${`${method}-summary`}
                        name=${`${method}-summary`}
                        type="text"
                        aria-label=${`Summary, ${M}`}
                        value=${op.summary || ''}
                        onInput=${onSummaryInput}
                        placeholder="List things"
                    />
                </div>

                <div class="query-parameters">
                    ${op.queryParameters.map((param, i) => {
                        const onName = (ev:Event) =>
                            State.UpdateQueryParameterField(
                                state, method, i, 'name',
                                (ev.currentTarget as HTMLInputElement)
                                    .value
                            )
                        const onType = (ev:Event) =>
                            State.UpdateQueryParameterField(
                                state, method, i, 'type',
                                (ev.currentTarget as HTMLSelectElement)
                                    .value
                            )
                        const onRequired = (ev:Event) =>
                            State.UpdateQueryParameterField(
                                state, method, i, 'required',
                                (ev.currentTarget as HTMLInputElement)
                                    .checked
                            )
                        const onDesc = (ev:Event) =>
                            State.UpdateQueryParameterField(
                                state, method, i, 'description',
                                (ev.currentTarget as HTMLInputElement)
                                    .value
                            )
                        const nameId = `${method}-query-param-${i}-name`
                        const nameLabel =
                            `Name, ${M} query parameter ${i + 1}`
                        const typeId = `${method}-query-param-${i}-type`
                        const typeLabel =
                            `Type, ${M} query parameter ${i + 1}`
                        const descId =
                            `${method}-query-param-${i}-description`
                        const descLabel =
                            `Description, ${M} query parameter ${i + 1}`
                        return html`<div
                            class="parameter-row query-param"
                        >
                            <${ToolTip.TAG}
                                delay=${1000}
                                content="delete this parameter"
                            >
                            <button type="button"
                                class="btn icon param-remove icon-x"
                                aria-label=${
                                    `Remove ${M} query parameter ` +
                                    (i + 1)
                                }
                                onClick=${() => {
                                    State.RemoveQueryParameter(
                                        state, method, i
                                    )
                                    focusAfterRender(() => {
                                        const scope =
                                            methodScope(method)
                                        if (!scope) return null
                                        const rows = scope
                                            .querySelectorAll<
                                                HTMLElement
                                            >(
                                                '.parameter-row' +
                                                '.query-param'
                                            )
                                        if (rows.length === 0) {
                                            return scope
                                                .querySelector<
                                                    HTMLElement
                                                >(
                                                    '.param-add'
                                                )
                                        }
                                        const prev = rows[
                                            Math.max(0, i - 1)
                                        ]
                                        return prev?.querySelector<
                                            HTMLElement
                                        >(
                                            '.param-remove'
                                        )
                                    }, { preventScroll: true })
                                }}
                            ><${IconX} /></button>
                            <//>

                            <div class="param-line">
                                <div class="param-field">
                                    <label
                                        class="field-label"
                                        for=${nameId}
                                    >Name</label>
                                    <input
                                        type="text"
                                        id=${nameId}
                                        name=${nameId}
                                        aria-label=${nameLabel}
                                        value=${param.name}
                                        placeholder="name"
                                        onInput=${onName}
                                    />
                                </div>
                                <${CheckBox.TAG}
                                    class="param-required"
                                    checked=${param.required}
                                    onChange=${onRequired}
                                >
                                    <span>Required</span>
                                <//>
                                <div class="param-field">
                                    <label
                                        class="field-label"
                                        for=${typeId}
                                    >Type</label>
                                    <select
                                        id=${typeId}
                                        name=${typeId}
                                        aria-label=${typeLabel}
                                        value=${param.type}
                                        onChange=${onType}
                                    >
                                        <option value="string">
                                            string
                                        </option>
                                        <option value="number">
                                            number
                                        </option>
                                        <option value="integer">
                                            integer
                                        </option>
                                        <option value="boolean">
                                            boolean
                                        </option>
                                    </select>
                                </div>
                            </div>
                            <div class="param-line">
                                <div class="param-field">
                                    <label
                                        class="field-label"
                                        for=${descId}
                                    >Description</label>
                                    <input
                                        type="text"
                                        id=${descId}
                                        name=${descId}
                                        aria-label=${descLabel}
                                        value=${param.description}
                                        placeholder="description"
                                        onInput=${onDesc}
                                    />
                                </div>
                            </div>
                        </div>`
                    })}
                </div>

                <button
                    type="button"
                    class="btn param-add"
                    onClick=${onAddQueryParameter}
                ><${IconPlus} />Add parameter</button>

                ${isBodyCapable ?
                    disclosureSection({
                        title: 'Request body',
                        open: false,
                        sectionKey:
                            `request-body-${routeContext}-${method}`,
                        contentClass: 'field request-body-field',
                        describedby: requestSchemaInvalid ?
                            `${method}-request-body-schema-error` :
                            requestExampleInvalid ?
                                `${method}-request-body-example-error` :
                                requestBodyModelError ?
                                    `${method}-request-body-model-error` :
                                    undefined,
                        children: html`
                            <fieldset
                                class="request-body-media-type"
                                aria-label=${
                                    `Request body media type, ${M}`
                                }
                            >
                                <legend class="field-label">
                                    Media type
                                </legend>
                                <div
                                    class="request-body-media-type-options"
                                >
                                    ${(REQUEST_BODY_MEDIA_TYPES as
                                        readonly string[]).map(mt =>
                                        html`<${RadioInput.TAG}
                                            key=${mt}
                                            name=${`${method}-request-body-media-type`}
                                            value=${mt}
                                            checked=${
                                                requestBodyMediaType === mt
                                            }
                                            label=${mt}
                                            onChange=${() =>
                                                State
                                                .UpdateRequestBodyField(
                                                    state, method,
                                                    'mediaType', mt
                                                )}
                                        ><//>`
                                    )}
                                </div>
                            </fieldset>
                            ${(() => {
                                const onGenerateRequestSchema = () => {
                                    const parsed = parseRequestBody(
                                        op.requestBodyExample
                                    )
                                    if (!parsed.ok) return
                                    State.UpdateRequestBodyField(
                                        state, method, 'schema',
                                        JSON.stringify(
                                            inferSchemaFromExample(
                                                parsed.value
                                            ), null, 2
                                        )
                                    )
                                }

                                const requestSchemaEditor =
                                    bodyFieldEditor({
                                        label: 'Schema',
                                        ariaLabel:
                                            `Request body schema, ${M}`,
                                        id:
                                            `${method}-request-body-schema`,
                                        value: op.requestBodySchema,
                                        json: true,
                                        invalid: requestSchemaInvalid,
                                        errorId:
                                            `${method}-request-body-schema-error`,
                                        placeholder:
                                            '{"type":"object"}',
                                        onInput: onRequestSchemaInput
                                    })
                                const requestExampleEditor =
                                    bodyFieldEditor({
                                        label: 'Example',
                                        ariaLabel:
                                            `Request body example, ${M}`,
                                        id:
                                            `${method}-request-body-example`,
                                        value: op.requestBodyExample,
                                        json: requestBodyIsJson,
                                        invalid: requestExampleInvalid,
                                        errorId:
                                            `${method}-request-body-example-error`,
                                        placeholder: requestBodyIsJson ?
                                            '{"name":"Rex"}' :
                                            'free-form text',
                                        onInput: onRequestExampleInput
                                    })

                                const generateButtonLabel =
                                    `Generate schema from example, ${M}`
                                const requestKind =
                                    mediaTypeKind(requestBodyMediaType)
                                const requestIsXml =
                                    isXmlMediaType(requestBodyMediaType)
                                let requestSchemaSurface:VNode | null = null
                                if (requestKind === 'object') {
                                    requestSchemaSurface = schemaBuilder({
                                        state,
                                        target: { kind: 'requestBody', method },
                                        method,
                                        isXml: requestIsXml,
                                        idBase:
                                            `${method}-request-body-prop`,
                                        labelBase:
                                            `${M} request body`,
                                        model:
                                            op.requestBodySchemaModel,
                                    })
                                } else if (requestKind === 'binary') {
                                    requestSchemaSurface =
                                        binarySchemaNote(requestBodyMediaType)
                                } else if (requestKind === 'text') {
                                    requestSchemaSurface = textSchemaNote()
                                }

                                const requestBodyArea =
                                    requestBodyIsJson ?
                                        html`
                                            ${requestExampleEditor}
                                            ${generateSchemaButton({
                                                ariaLabel:
                                                    generateButtonLabel,
                                                exampleText:
                                                    op.requestBodyExample,
                                                onGenerate:
                                                    onGenerateRequestSchema
                                            })}
                                            ${requestSchemaEditor}
                                        ` :
                                        html`
                                            ${requestSchemaSurface}
                                            ${requestExampleEditor}
                                        `

                                return requestBodyArea
                            })()}
                        `
                    }) :
                    null}

                ${requestSchemaInvalid ?
                    html`<p
                        id=${`${method}-request-body-schema-error`}
                        class="message error request-body-error"
                        role="alert"
                    >${requestSchemaParse.ok ?
                        '' :
                        requestSchemaParse.error}</p>` :
                    null}

                ${requestExampleInvalid ?
                    html`<p
                        id=${`${method}-request-body-example-error`}
                        class="message error request-body-error"
                        role="alert"
                    >${requestExampleParse.ok ?
                        '' :
                        requestExampleParse.error}</p>` :
                    null}

                ${requestBodyModelError ?
                    html`<p
                        id=${`${method}-request-body-model-error`}
                        class="message error request-body-error"
                        role="alert"
                    >${requestBodyModelError}</p>` :
                    null}

                ${disclosureSection({
                    title: 'Responses',
                    open: false,
                    sectionKey:
                        `responses-${routeContext}-${method}`,
                    contentClass: 'field responses-section',
                    describedby: responsesInvalid ?
                        `${method}-responses-error` :
                        undefined,
                    summaryExtra: responsesSummary.count > 0 ?
                        html`<span
                            class="responses-summary"
                            aria-hidden="true"
                        ><span class="count"
                            >(${responsesSummary.count})</span
                        ><span class="codes">${
                            responsesSummary.codes.join(' ')
                        }${responsesSummary.overflowCount ?
                            ` +${responsesSummary.overflowCount}` :
                            ''}</span></span>` :
                        undefined,
                    children: html`
                    ${op.responses.map((resp, i) => {
                        const isCustom = !(
                            CURATED_STATUS_CODES as
                            readonly string[]
                        ).includes(resp.status)
                        const respIsJson = isJsonMediaType(
                            resp.mediaType.trim() ||
                                'application/json'
                        )
                        const onSelectStatus = (
                            ev:Event
                        ) => {
                            const sel = ev
                                .currentTarget as
                                HTMLSelectElement
                            State.UpdateResponseField(
                                state,
                                method,
                                i,
                                'status',
                                sel.value === 'custom' ?
                                    '' :
                                    sel.value
                            )
                        }
                        const onCustomInput = (ev:Event) => {
                            const inp = ev.currentTarget as
                                HTMLInputElement
                            State.UpdateResponseField(
                                state,
                                method,
                                i,
                                'status',
                                inp.value
                            )
                        }
                        const onDescInput = (ev:Event) => {
                            const inp = ev.currentTarget as
                                HTMLInputElement
                            State.UpdateResponseField(
                                state,
                                method,
                                i,
                                'description',
                                inp.value
                            )
                        }
                        const onSchemaInput = (ev:Event) => {
                            const ta = ev
                                .currentTarget as
                                HTMLTextAreaElement
                            State.UpdateResponseField(
                                state,
                                method,
                                i,
                                'schema',
                                ta.value
                            )
                        }
                        const onExampleInput = (ev:Event) => {
                            const ta = ev
                                .currentTarget as
                                HTMLTextAreaElement
                            State.UpdateResponseField(
                                state,
                                method,
                                i,
                                'example',
                                ta.value
                            )
                        }
                        const onCustomMediaInput = (ev:Event) => {
                            const inp = ev
                                .currentTarget as
                                HTMLInputElement
                            State.UpdateResponseField(
                                state,
                                method,
                                i,
                                'mediaType',
                                inp.value
                            )
                        }
                        const onRemoveResponse = () => {
                            State.RemoveResponse(state, method, i)
                            focusAfterRender(() => {
                                const scope =
                                    methodScope(method)
                                if (!scope) return null
                                const rows = scope
                                    .querySelectorAll<
                                        HTMLElement
                                    >(
                                        '.response-item'
                                    )
                                if (rows.length === 0) {
                                    return scope
                                        .querySelector<
                                            HTMLElement
                                        >(
                                            '.responses-section' +
                                            ' .param-add'
                                        )
                                }
                                const prev = rows[
                                    Math.max(0, i - 1)
                                ]
                                return prev?.querySelector<
                                    HTMLElement
                                >(
                                    '.param-remove'
                                )
                            }, { preventScroll: true })
                        }
                        const isCustomMediaType = !(
                            CURATED_MEDIA_TYPES as
                            readonly string[]
                        ).includes(resp.mediaType)
                        const mediaGroupName =
                            `${method}-response-${i}-media-type`
                        const mediaGroupLabel =
                            `Media type, ${M} response ${i + 1}`
                        const customMediaId =
                            `${method}-response-${i}-media-type-custom`
                        const customMediaLabel =
                            `Custom media type, ${M} response ${i + 1}`
                        const onPickMediaType =
                            (mt:string) => () =>
                                State.UpdateResponseField(
                                    state,
                                    method,
                                    i,
                                    'mediaType',
                                    mt
                                )
                        const onClearMediaType = () =>
                            State.UpdateResponseField(
                                state,
                                method,
                                i,
                                'mediaType',
                                ''
                            )
                        const statusId =
                            `${method}-response-${i}-status`
                        const statusLabel =
                            `Status, ${M} response ${i + 1}`
                        const codeId =
                            `${method}-response-${i}-code`
                        const codeLabel =
                            `Code, ${M} response ${i + 1}`
                        const descId =
                            `${method}-response-${i}-description`
                        const descLabel =
                            `Description, ${M} response ${i + 1}`
                        const trimmedStatus = resp.status.trim()
                        const trimmedDesc = resp.description.trim()
                        const summaryText = trimmedStatus ?
                            (trimmedDesc ?
                                `${trimmedStatus} ${trimmedDesc}` :
                                trimmedStatus) :
                            'New response'
                        const summaryLabel =
                            `${summaryText}, ${M} response ${i + 1}`
                        return html`<div class="response-item">
                            <${ToolTip.TAG}
                                delay=${1000}
                                content="delete this response"
                            >
                                <${Button}
                                    type="button"
                                    class="danger icon param-remove icon-trash"
                                    aria-label=${
                                        `Remove ${M} response ` + (i + 1)
                                    }
                                    onClick=${onRemoveResponse}
                                ><${IconTrash} /><//>
                            <//>
                            ${disclosureSection({
                                title: summaryText,
                                summaryLabel,
                                summaryAside: html`<span
                                    class="add-body-hint"
                                    aria-hidden="true"
                                >add response body</span>`,
                                open: resp.expanded,
                                sectionKey:
                                    `response-${routeContext}-${method}-${i}`,
                                contentClass: 'response-item-content',
                                children: html`
                                    <div class="response-status-cell">
                                        <div class="param-field">
                                            <label
                                                class="field-label"
                                                for=${statusId}
                                            >Status</label>
                                            <select
                                                id=${statusId}
                                                name=${statusId}
                                                aria-label=${statusLabel}
                                                value=${isCustom ?
                                                    'custom' :
                                                    resp.status}
                                                onChange=${onSelectStatus}
                                            >
                                                ${(
                                                    CURATED_STATUS_CODES as
                                                    readonly string[]
                                                ).map(
                                                    code =>
                                                    html`<option
                                                        value=${code}
                                                    >${code}</option>`
                                                )}
                                                <option value="custom">
                                                    custom
                                                </option>
                                            </select>
                                        </div>
                                        ${isCustom ?
                                            html`<div class="param-field">
                                                <label
                                                    class="field-label"
                                                    for=${codeId}
                                                >Code</label>
                                                <input
                                                    type="number"
                                                    id=${codeId}
                                                    name=${codeId}
                                                    aria-label=${codeLabel}
                                                    inputmode="numeric"
                                                    value=${resp.status}
                                                    placeholder="code"
                                                    onInput=${onCustomInput}
                                                />
                                            </div>` :
                                            null}
                                    </div>
                                    <div class="param-field">
                                        <label
                                            class="field-label"
                                            for=${descId}
                                        >Description</label>
                                        <input
                                            type="text"
                                            id=${descId}
                                            name=${descId}
                                            aria-label=${descLabel}
                                            value=${resp.description}
                                            placeholder="description"
                                            onInput=${onDescInput}
                                        />
                                    </div>
                                    <fieldset
                                        class="response-media-type"
                                        aria-label=${mediaGroupLabel}
                                    >
                                        <legend
                                            class="field-label"
                                        >Media type</legend>
                                        <div
                                        class="response-media-type-options"
                                        >
                                        ${(
                                            CURATED_MEDIA_TYPES as
                                            readonly string[]
                                        ).map(mt =>
                                            html`<${RadioInput.TAG}
                                                key=${mt}
                                                name=${mediaGroupName}
                                                value=${mt}
                                                checked=${
                                                    resp.mediaType === mt
                                                }
                                                label=${mt}
                                                onChange=${
                                                    onPickMediaType(mt)
                                                }
                                            ><//>`
                                        )}
                                        <${RadioInput.TAG}
                                            key="custom"
                                            name=${mediaGroupName}
                                            value="custom"
                                            checked=${isCustomMediaType}
                                            label="custom"
                                            onChange=${onClearMediaType}
                                        ><//>
                                        ${isCustomMediaType ?
                                            html`<input
                                                type="text"
                                                id=${customMediaId}
                                                name=${customMediaId}
                                                aria-label=${
                                                    customMediaLabel
                                                }
                                                value=${resp.mediaType}
                                                placeholder="type/subtype"
                                                onInput=${
                                                    onCustomMediaInput
                                                }
                                            />` :
                                            null}
                                        </div>
                                    </fieldset>
                                    ${responseBodyEditor({
                                        state,
                                        method,
                                        i,
                                        resp,
                                        M,
                                        respIsJson,
                                        onSchemaInput,
                                        onExampleInput
                                    })}
                                `,
                            })}
                        </div>`
                    })}
                    <div class="controls">
                        <button type="button"
                            class="btn link param-add"
                            onClick=${onAddResponse}
                        ><span>
                                <${IconPlus} />Add response
                            </span></button>
                    </div>
                `
                })}

                ${responsesInvalid ?
                    html`<p
                        id=${`${method}-responses-error`}
                        class="message error responses-error"
                        role="alert"
                    >
                        ${responsesError}
                    </p>` :
                    null
                }
            `,
    })
}

// Move focus after Preact flushes the signal-driven DOM update. The target
// row/control exists by the next animation frame, so dynamic-row changes do
// not drop keyboard focus to <body>. (WCAG 2.4.3)
function focusAfterRender (
    select:() => HTMLElement | null | undefined,
    options?:FocusOptions
):void {
    requestAnimationFrame(() => {
        select()?.focus(options)
    })
}

// Schema builder constants and context
const SCHEMA_TYPE_OPTIONS:SchemaNodeType[] = [
    'string', 'number', 'integer', 'boolean', 'object', 'array'
]
const STRING_FORMAT_OPTIONS:StringFormat[] = [
    'date-time', 'date', 'email', 'uri', 'uuid', 'password'
]

interface BuilderCtx {
    state:ReturnType<typeof State>;
    target:SchemaTarget;
    method:string;
    isXml:boolean;
    idBase:string;
    labelBase:string;
    path:SchemaPath;
}

// Focus the new property's name input within the live method section.
function focusNewProperty (
    method:string,
    nodeIdBase:string,
    newIndex:number
) {
    focusAfterRender(() =>
        methodScope(method)?.querySelector<HTMLElement>(
            `[id="${nodeIdBase}-${newIndex}-name"]`
        )
    )
}

// Focus after removing property `index` from a node of `prevLen` properties.
function focusAfterPropertyRemove (
    method:string,
    nodeIdBase:string,
    index:number,
    prevLen:number
) {
    const remaining = prevLen - 1
    const selector = remaining === 0 ?
        `[id="${nodeIdBase}-add"]` :
        `[id="${nodeIdBase}-${Math.max(0, index - 1)}-remove"]`
    focusAfterRender(
        () => methodScope(method)?.querySelector<HTMLElement>(selector),
        { preventScroll: true }
    )
}

function schemaBuilder (
    ctx:Omit<BuilderCtx, 'path'> & { model?:SchemaNode }
) {
    const root = (ctx.model && ctx.model.type === 'object') ?
        ctx.model :
        emptySchemaNode()
    const rootCtx:BuilderCtx = {
        state: ctx.state,
        target: ctx.target,
        method: ctx.method,
        isXml: ctx.isXml,
        idBase: ctx.idBase,
        labelBase: ctx.labelBase,
        path: [],
    }
    const rootXml = ctx.isXml ?
        xmlControls(rootCtx, root, { attribute: false, wrapped: false }) :
        null
    return html`${rootXml}${objectProperties({
        ...ctx,
        path: [],
        node: root,
    })}`
}

function objectProperties (ctx:BuilderCtx & { node:SchemaNode }) {
    const { state, target, method, isXml, idBase, labelBase, path, node } = ctx
    const props = node.properties || []
    return html`<div class="schema-object">
        ${props.map((prop, i) => propertyRow({
            state,
            target,
            method,
            isXml,
            idBase,
            labelBase,
            path,
            prop,
            index: i,
            siblingCount: props.length,
        }))}
        <button
            type="button"
            class="btn link param-add schema-add"
            id=${`${idBase}-add`}
            aria-label=${`Add property, ${labelBase}`}
            onClick=${() => {
                const newIndex = (node.properties || []).length
                State.AddProperty(state, target, path)
                focusNewProperty(method, idBase, newIndex)
            }}
        ><span>Add property</span></button>
    </div>`
}

function stringExtras (
    propIdBase:string,
    propLabel:string,
    stype:SchemaNodeType,
    isFile:boolean,
    format:StringFormat | undefined,
    contentMediaType:string | undefined,
    onFormat:(ev:Event) => void,
    onFile:(ev:Event) => void,
    onCmt:(ev:Event) => void
) {
    if (stype !== 'string') return null
    return html`<div class="schema-row-line2">
        ${!isFile ?
            html`<label class="param-field">
                <span class="field-label">Format</span>
                <select
                    id=${`${propIdBase}-format`}
                    name=${`${propIdBase}-format`}
                    aria-label=${`Format, ${propLabel}`}
                    value=${format ?? ''}
                    onChange=${onFormat}
                ><option value="">(none)</option>
                ${STRING_FORMAT_OPTIONS.map(fmt =>
                    html`<option key=${fmt} value=${fmt}>${fmt}</option>`
                )}</select>
            </label>` :
            null}
        <label class="param-field">
            <span class="field-label">File</span>
            <input
                type="checkbox"
                id=${`${propIdBase}-file`}
                name=${`${propIdBase}-file`}
                aria-label=${`File field, ${propLabel}`}
                checked=${isFile}
                onChange=${onFile}
            />
        </label>
        ${isFile ?
            html`<label class="param-field">
                <span class="field-label">Media type</span>
                <input
                    type="text"
                    id=${`${propIdBase}-cmt`}
                    name=${`${propIdBase}-cmt`}
                    aria-label=${`Content media type, ${propLabel}`}
                    value=${contentMediaType ?? ''}
                    placeholder="application/octet-stream"
                    onInput=${onCmt}
                />
            </label>` :
            null}
    </div>`
}

function xmlControls (
    ctx:BuilderCtx,
    node:SchemaNode,
    opts:{ attribute:boolean; wrapped:boolean }
) {
    const { state, target, idBase, labelBase, path } = ctx
    const xml = node.xml || {}
    const set = (
        field:'name' | 'namespace' | 'prefix' | 'attribute' | 'wrapped'
    ) => (ev:Event) => {
        const el = ev.target as HTMLInputElement
        const value = field === 'attribute' || field === 'wrapped' ?
            el.checked :
            el.value
        State.UpdateNodeXml(state, target, path, field, value)
    }
    return html`<fieldset class="schema-xml" aria-label=${`XML, ${labelBase}`}>
        <legend class="field-label">XML</legend>
        <label class="param-field">
            <span class="field-label">Name</span>
            <input
                type="text"
                id=${`${idBase}-xml-name`}
                name=${`${idBase}-xml-name`}
                aria-label=${`XML name, ${labelBase}`}
                value=${xml.name ?? ''}
                onInput=${set('name')}
            />
        </label>
        <label class="param-field">
            <span class="field-label">Namespace</span>
            <input
                type="text"
                id=${`${idBase}-xml-namespace`}
                name=${`${idBase}-xml-namespace`}
                aria-label=${`XML namespace, ${labelBase}`}
                value=${xml.namespace ?? ''}
                onInput=${set('namespace')}
            />
        </label>
        <label class="param-field">
            <span class="field-label">Prefix</span>
            <input
                type="text"
                id=${`${idBase}-xml-prefix`}
                name=${`${idBase}-xml-prefix`}
                aria-label=${`XML prefix, ${labelBase}`}
                value=${xml.prefix ?? ''}
                onInput=${set('prefix')}
            />
        </label>
        ${opts.attribute ?
            html`<label class="param-field">
                <span class="field-label">Attribute</span>
                <input
                    type="checkbox"
                    id=${`${idBase}-xml-attribute`}
                    name=${`${idBase}-xml-attribute`}
                    aria-label=${`XML attribute, ${labelBase}`}
                    checked=${xml.attribute === true}
                    onChange=${set('attribute')}
                />
            </label>` :
            null}
        ${opts.wrapped ?
            html`<label class="param-field">
                <span class="field-label">Wrapped</span>
                <input
                    type="checkbox"
                    id=${`${idBase}-xml-wrapped`}
                    name=${`${idBase}-xml-wrapped`}
                    aria-label=${`XML wrapped, ${labelBase}`}
                    checked=${xml.wrapped === true}
                    onChange=${set('wrapped')}
                />
            </label>` :
            null}
    </fieldset>`
}

function propertyRow (ctx:BuilderCtx & {
    prop:PropertyEntry; index:number; siblingCount:number;
}) {
    const {
        state, target, method, isXml, idBase, labelBase, path,
        prop, index, siblingCount,
    } = ctx
    const propIdBase = `${idBase}-${index}`
    const propLabel = `${labelBase} property ${index + 1}`
    const childPath:SchemaPath = [...path, { kind: 'property', index }]
    const stype = prop.schema.type
    const isFile = prop.schema.file === true

    const onName = (ev:Event) => State.UpdatePropertyName(
        state, target, path, index,
        (ev.target as HTMLInputElement).value
    )
    const onType = (ev:Event) => {
        State.UpdatePropertyType(
            state, target, childPath,
            (ev.target as HTMLSelectElement).value as SchemaNodeType
        )
        focusAfterRender(() =>
            methodScope(method)?.querySelector<HTMLElement>(
                `[id="${propIdBase}-type"]`
            )
        )
    }
    const onRequired = (ev:Event) => State.UpdatePropertyRequired(
        state, target, path, index,
        (ev.target as HTMLInputElement).checked
    )
    const onRemove = () => {
        State.RemoveProperty(state, target, path, index)
        focusAfterPropertyRemove(method, idBase, index, siblingCount)
    }
    const onFormat = (ev:Event) => State.UpdatePropertyFormat(
        state, target, childPath,
        (ev.target as HTMLSelectElement).value as StringFormat | ''
    )
    const onFile = (ev:Event) => State.SetPropertyFile(
        state, target, childPath,
        (ev.target as HTMLInputElement).checked
    )
    const onCmt = (ev:Event) => State.UpdatePropertyContentMediaType(
        state, target, childPath,
        (ev.target as HTMLInputElement).value
    )

    // line 1: Name | Type | Required | Remove
    // line 2 (string, not file): Format + File toggle
    // line 2 (string + file): File toggle + contentMediaType input
    // nested block: object -> objectProperties(childPath);
    //               array  -> arrayItem(childPath)
    const nested = stype === 'object' ?
        html`<div class="schema-children">
            ${objectProperties({
                state,
                target,
                method,
                isXml,
                idBase: propIdBase,
                labelBase: propLabel,
                path: childPath,
                node: prop.schema,
            })}
        </div>` :
        stype === 'array' ?
            html`<div class="schema-children">
                ${arrayItem({
                    state,
                    target,
                    method,
                    isXml,
                    idBase: propIdBase,
                    labelBase: propLabel,
                    path: childPath,
                    node: prop.schema,
                })}
            </div>` :
            null

    const propCtx:BuilderCtx = {
        state,
        target,
        method,
        isXml,
        idBase: propIdBase,
        labelBase: propLabel,
        path: childPath,
    }
    const propXml = isXml ?
        xmlControls(propCtx, prop.schema,
            { attribute: true, wrapped: stype === 'array' }) :
        null

    return html`<div class="schema-row" data-type=${stype}>
        <label class="param-field">
            <span class="field-label">Name</span>
            <input
                type="text"
                id=${`${propIdBase}-name`}
                name=${`${propIdBase}-name`}
                aria-label=${`Name, ${propLabel}`}
                value=${prop.name}
                onInput=${onName}
            />
        </label>
        <label class="param-field">
            <span class="field-label">Type</span>
            <select
                id=${`${propIdBase}-type`}
                name=${`${propIdBase}-type`}
                aria-label=${`Type, ${propLabel}`}
                value=${stype}
                onChange=${onType}
            >${SCHEMA_TYPE_OPTIONS.map(t =>
                html`<option key=${t} value=${t}>${t}</option>`
            )}</select>
        </label>
        <label class="param-field">
            <span class="field-label">Required</span>
            <input
                type="checkbox"
                id=${`${propIdBase}-required`}
                name=${`${propIdBase}-required`}
                aria-label=${`Required, ${propLabel}`}
                checked=${prop.required}
                onChange=${onRequired}
            />
        </label>
        <button
            type="button"
            class="btn icon param-remove"
            id=${`${propIdBase}-remove`}
            aria-label=${`Remove ${propLabel}`}
            onClick=${onRemove}
        ><span>×</span></button>
        ${stringExtras(
            propIdBase, propLabel, stype, isFile,
            prop.schema.format, prop.schema.contentMediaType,
            onFormat, onFile, onCmt
        )}
        ${nested}
        ${propXml}
    </div>`
}

function arrayItem (ctx:BuilderCtx & { node:SchemaNode }) {
    const { state, target, method, isXml, idBase, labelBase, path, node } = ctx
    const itemIdBase = `${idBase}-item`
    const itemLabel = `${labelBase} item`
    const itemsPath:SchemaPath = [...path, { kind: 'items' }]
    const item = node.items || { type: 'string' }
    const onItemType = (ev:Event) => {
        State.UpdatePropertyType(
            state, target, itemsPath,
            (ev.target as HTMLSelectElement).value as SchemaNodeType
        )
        focusAfterRender(() =>
            methodScope(method)?.querySelector<HTMLElement>(
                `[id="${itemIdBase}-type"]`
            )
        )
    }
    const nested = item.type === 'object' ?
        objectProperties({
            state,
            target,
            method,
            isXml,
            idBase: itemIdBase,
            labelBase: itemLabel,
            path: itemsPath,
            node: item,
        }) :
        item.type === 'array' ?
            arrayItem({
                state,
                target,
                method,
                isXml,
                idBase: itemIdBase,
                labelBase: itemLabel,
                path: itemsPath,
                node: item,
            }) :
            null

    const itemCtx:BuilderCtx = {
        state,
        target,
        method,
        isXml,
        idBase: itemIdBase,
        labelBase: itemLabel,
        path: itemsPath,
    }
    const itemXml = isXml ?
        xmlControls(itemCtx, item, { attribute: true, wrapped: false }) :
        null

    return html`<div class="schema-array">
        <label class="param-field">
            <span class="field-label">Item type</span>
            <select
                id=${`${itemIdBase}-type`}
                name=${`${itemIdBase}-type`}
                aria-label=${`Item type, ${itemLabel}`}
                value=${item.type}
                onChange=${onItemType}
            >${SCHEMA_TYPE_OPTIONS.map(t =>
                html`<option key=${t} value=${t}>${t}</option>`
            )}</select>
        </label>
        ${nested}
        ${itemXml}
    </div>`
}

// Freeze a section's initial open state per editing context. `contextKey`
// changes only when the user selects a different route, starts a new route,
// or switches the active method; within a context the same value is
// returned every render, so removing a row never re-asserts `open` and
// collapses the section (which would drop focus to <body>). (WCAG 2.4.3)
function useInitialOpen (
    contextKey:string,
    populated:boolean,
):boolean {
    const ref = useRef<{ key:string; open:boolean }>()
    if (!ref.current || ref.current.key !== contextKey) {
        ref.current = { key: contextKey, open: populated }
    }
    return ref.current.open
}

function disclosureSection (opts:{
    title:string;
    open:boolean;
    sectionKey:string;
    contentClass:string;
    children:unknown;
    describedby?:string;
    summaryExtra?:unknown;
    summaryAside?:unknown;
    summaryLabel?:string;
}) {
    const {
        title, open, sectionKey, contentClass, children, summaryExtra
    } = opts
    return html`<${DetailsSummary.TAG} key=${sectionKey}>
        <details open=${open}>
            <summary aria-label=${opts.summaryLabel}>${summaryExtra ?
                html`<span class="summary-heading"
                    >${title}${summaryExtra}</span>` :
                title}${opts.summaryAside}</summary>
            <div
                class=${`details-content ${contentClass}`}
                role="group"
                aria-label=${opts.summaryLabel ?? title}
                aria-describedby=${opts.describedby}
            >${children}</div>
        </details>
    <//>`
}

function binarySchemaNote (mediaType:string) {
    const base = (mediaType || '').split(';')[0].trim()
    return html`<p class="schema-note" role="note">
        Binary body. Schema is derived automatically as
        <code>{ type: "string", contentMediaType: "${base}" }</code>.
    </p>`
}

function textSchemaNote () {
    return html`<p class="schema-note" role="note">
        Text body. Schema is derived automatically as
        <code>{ type: "string" }</code>.
    </p>`
}

function responseBodyEditor (opts:{
    state:ReturnType<typeof State>;
    method:string;
    i:number;
    resp:ResponseEntry;
    M:string;
    respIsJson:boolean;
    onSchemaInput:(ev:Event) => void;
    onExampleInput:(ev:Event) => void;
}) {
    const {
        state, method, i, resp, M, respIsJson,
        onSchemaInput, onExampleInput
    } = opts

    const onGenerateResponseSchema = () => {
        const parsed = parseRequestBody(resp.example || '')
        if (!parsed.ok) return
        State.UpdateResponseField(
            state, method, i,
            'schema',
            JSON.stringify(
                inferSchemaFromExample(parsed.value), null, 2
            )
        )
    }

    const schemaId = `${method}-response-${i}-schema`
    const exampleId = `${method}-response-${i}-example`
    const genBtnLabel = 'Generate schema from example, ' +
        `${M} response ${i + 1}`

    const responseSchemaEditor = bodyFieldEditor({
        label: 'Schema',
        ariaLabel: `Schema, ${M} response ${i + 1} (JSON)`,
        id: schemaId,
        value: resp.schema || '',
        json: true,
        onInput: onSchemaInput
    })

    const responseExampleEditor = bodyFieldEditor({
        label: 'Example',
        ariaLabel: respIsJson ?
            `Example, ${M} response ${i + 1} (JSON)` :
            `Example, ${M} response ${i + 1}`,
        id: exampleId,
        value: resp.example || '',
        json: respIsJson,
        onInput: onExampleInput
    })

    const responseMediaType =
        resp.mediaType.trim() || 'application/json'
    const responseKind = mediaTypeKind(responseMediaType)
    const responseIsXml = isXmlMediaType(responseMediaType)

    let responseSchemaSurface:VNode | null = null
    if (responseKind === 'object') {
        const respIdBase = `${method}-response-${i}-prop`
        const respLabelBase = `${M} response ${i + 1}`
        responseSchemaSurface = schemaBuilder({
            state,
            target: {
                kind: 'response',
                method,
                index: i,
            },
            method,
            isXml: responseIsXml,
            idBase: respIdBase,
            labelBase: respLabelBase,
            model: resp.schemaModel,
        })
    } else if (responseKind === 'binary') {
        responseSchemaSurface = binarySchemaNote(
            resp.mediaType
        )
    } else if (responseKind === 'text') {
        responseSchemaSurface = textSchemaNote()
    }

    const responseBodyArea = respIsJson ?
        html`
            ${responseExampleEditor}
            ${generateSchemaButton({
                ariaLabel: genBtnLabel,
                exampleText: resp.example || '',
                onGenerate: onGenerateResponseSchema
            })}
            ${responseSchemaEditor}
        ` :
        html`
            ${responseSchemaSurface}
            ${responseExampleEditor}
        `

    return responseBodyArea
}

function bodyFieldEditor (opts:{
    label:string;
    ariaLabel:string;
    id:string;
    value:string;
    json:boolean;
    onInput:(ev:Event)=>void;
    invalid?:boolean;
    errorId?:string;
    placeholder?:string;
}) {
    const { label, ariaLabel, id, value, json, onInput } = opts
    const invalid = opts.invalid ?? false
    const placeholder = opts.placeholder ?? ''
    let previewHtml = ''
    if (json && value.trim() !== '') {
        const parsed = parseRequestBody(value)
        previewHtml = parsed.ok ?
            highlightJson(JSON.stringify(parsed.value, null, 2)) :
            ''
    }
    const previewNode = json ?
        html`<code
            class="language-javascript"
            dangerouslySetInnerHTML=${{ __html: previewHtml }}
        ></code>` :
        html`<code class="language-plaintext">${value}</code>`
    return html`<div class="body-field">
        <label class="field-label" for=${id}>${label}</label>
        <div class="body-field-cols">
            <div class="body-field-col">
                <textarea
                    id=${id}
                    name=${id}
                    aria-label=${ariaLabel}
                    aria-invalid=${invalid ? 'true' : 'false'}
                    aria-describedby=${invalid && opts.errorId ?
                        opts.errorId :
                        undefined}
                    onInput=${onInput}
                    placeholder=${placeholder}
                >${value}</textarea>
            </div>
            <div class="body-field-col">
                <pre
                    class="body-field-output"
                    role="group"
                    aria-label=${`${ariaLabel} preview`}
                >${previewNode}</pre>
            </div>
        </div>
    </div>`
}

function generateSchemaButton (opts:{
    ariaLabel:string;
    exampleText:string;
    onGenerate:() => void;
}) {
    const parse = parseRequestBody(opts.exampleText)
    const disabled = opts.exampleText.trim() === '' || !parse.ok
    return html`<button
        type="button"
        class="btn link generate-schema"
        aria-label=${opts.ariaLabel}
        disabled=${disabled}
        onClick=${opts.onGenerate}
    ><span>Generate schema from example</span></button>`
}
